// RewardRow.tsx

import GoldIcon from "../../../../../../../assets/icons/gold-icon.svg?react";
import UsdcIcon from "../../../../../../../assets/icons/usdc-icon.svg?react";
import WldIcon from "../../../../../../../assets/icons/wld-icon.svg?react";
import QuarkLogoImg from "../../../../../../../assets/images/coins/quark-coin.webp";
import ReddLogoImg from "../../../../../../../assets/images/coins/redd-icon.webp";
import SushiLogoImg from "../../../../../../../assets/images/coins/sushi-icon.webp";
import OroLogoImg from "../../../../../../../assets/images/coins/oro-icon.webp";
import OrbLogoImg from "../../../../../../../assets/images/coins/orb-icon.webp";
import "./RewardRow.scss";

// Minimal local reward type compatible with feed data
type Reward = {
    unit: "USDC" | "WLD" | "GOLD" | "EXP" | string;
    amount: number | string;
};

interface RewardRowProps {
    reward: Reward;
    isPrimary?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
}

// A helper component to render the correct icon based on the reward unit
const RewardIcon = ({ reward }: { reward: Reward }) => {
    // Handle progression rewards (like XP)
    if (reward.unit === "EXP") {
        return (
            <div className="container__row__icon bg-green-purple-gradient">
                <p>XP</p>
            </div>
        );
    }

    const baseClass = "container__row__icon";

    switch (reward.unit) {
        case "GOLD":
            return (
                <div className={`${baseClass} bg-none icon-gold`}>
                    <GoldIcon />
                </div>
            );
        case "USDC":
            return (
                <div className={`${baseClass} bg-none icon-usdc`}>
                    <UsdcIcon />
                </div>
            );
        case "WLD":
            return (
                <div className={`${baseClass} bg-none icon-wld`}>
                    <WldIcon />
                </div>
            );
        case "QUARK":
            return (
                <div className={`${baseClass} bg-none icon-quark`}>
                    <img src={QuarkLogoImg} alt="" />
                </div>
            );
        case "REDD":
            return (
                <div className={`${baseClass} bg-none icon-redd`}>
                    <img src={ReddLogoImg} alt="" />
                </div>
            );
        case "SUSHI":
            return (
                <div className={`${baseClass} bg-none icon-sushi`}>
                    <img src={SushiLogoImg} alt="" />
                </div>
            );
        case "ORO":
            return (
                <div className={`${baseClass} bg-none icon-oro`}>
                    <img src={OroLogoImg} alt="" />
                </div>
            );
        case "ORB":
            return (
                <div className={`${baseClass} bg-none icon-orb`}>
                    <img src={OrbLogoImg} alt="" />
                </div>
            );
        default:
            return null; // or a default icon
    }
};

// A helper component to render the reward text
const RewardText = ({ reward }: { reward: Reward }) => {
    const amountNum =
        typeof reward.amount === "string"
            ? Number(reward.amount)
            : reward.amount;

    // Handle progression rewards
    if (reward.unit === "EXP") {
        return (
            <div className="container__row__text">
                <p>
                    {Number.isFinite(amountNum)
                        ? amountNum.toLocaleString()
                        : "0"}
                </p>
                <p>XP</p>
            </div>
        );
    }

    // Handle crypto rewards
    const IconComponent =
        reward.unit === "GOLD"
            ? GoldIcon
            : reward.unit === "USDC"
              ? UsdcIcon
              : WldIcon;

    return (
        <div className="container__row__text">
            <p>{Number.isFinite(amountNum) ? amountNum.toFixed(2) : "0.00"}</p>

            {reward.unit === "GOLD" && (
                <IconComponent className="inline-unit-icon" />
            )}
            <p className="inline-unit-desc">({reward.unit})</p>
        </div>
    );
};

export default function RewardRow({
    reward,
    isPrimary = false,
    isFirst = false,
    isLast = false,
}: RewardRowProps) {
    // Dynamically build the class name

    const rowClassName = [
        "container__row",
        isPrimary ? "primary-reward" : "secondary-reward",
        isFirst ? "first" : "",
        isLast ? "last" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={rowClassName}>
            <RewardIcon reward={reward} />
            <RewardText reward={reward} />
        </div>
    );
}
